import { Injectable, computed, signal } from '@angular/core';

import { CompanyCategory, UserCategory } from '../models/core.models';

/**
 * The categorize dialog's working copy of the company category tree.
 *
 * The tree itself is fetched (`injectPostUserCategoriesQuery`); the checkbox
 * state on top of it is client state the dialog builds and reads back on save.
 *
 * Root-provided rather than dialog-provided: the tab bodies are created through a
 * `ViewContainerRef`, so a component-level provider would depend on the dynamic
 * component inheriting the dialog's element injector. Only one categorize dialog
 * is ever open.
 */
@Injectable({ providedIn: 'root' })
export class PostUserCategoriesEditorService {
  private readonly tree = signal<CompanyCategory[] | null>(null);

  readonly categories = this.tree.asReadonly();

  readonly hasActiveCategories = computed(() =>
    (this.tree() ?? []).some((companyCategory) =>
      companyCategory.categories.some((category) => category.is_selected),
    ),
  );

  /** `{ [companyCategoryId]: categoryId[] }` - the dialog's save payload. */
  readonly groupedSelectedCategories = computed(() => {
    const grouped: { [companyCategoryId: number]: number[] } = {};

    (this.tree() ?? []).forEach((companyCategory) =>
      collectSelected(companyCategory.categories, grouped),
    );

    return grouped;
  });

  /** Seeds the working copy from the fetched tree. */
  setTree(categories: CompanyCategory[] | null): void {
    this.tree.set(categories);
  }

  categoryById(companyCategoryId: number): CompanyCategory | null {
    return (
      this.tree()?.find(
        (category) => category.company_category_id === companyCategoryId,
      ) ?? null
    );
  }

  toggleSelection(
    companyCategoryId: number,
    categoryId: number,
    selected: boolean,
  ): void {
    this.tree.update((tree) =>
      (tree ?? []).map((companyCategory) =>
        companyCategory.company_category_id === companyCategoryId
          ? {
              ...companyCategory,
              categories: selected
                ? checkCategoryAndParent(companyCategory.categories, categoryId)
                : uncheckCategoryAndChildren(
                    companyCategory.categories,
                    categoryId,
                  ),
            }
          : companyCategory,
      ),
    );
  }

  /** Clears every selected category across all tabs. */
  clearSelected(): void {
    (this.tree() ?? []).forEach((companyCategory) =>
      companyCategory.categories.forEach((category) => {
        if (category.is_selected) {
          this.toggleSelection(
            category.company_category_id,
            category.id,
            false,
          );
        }
      }),
    );
  }
}

const collectSelected = (
  categories: UserCategory[],
  acc: { [companyCategoryId: number]: number[] },
) => {
  categories.forEach((category) => {
    if (category.is_selected) {
      if (!acc[category.company_category_id]) {
        acc[category.company_category_id] = [];
      }
      acc[category.company_category_id].push(category.id);
    }

    if (category.children && category.children.length > 0) {
      collectSelected(category.children, acc);
    }
  });
};

const checkCategoryAndParent = (
  categories: UserCategory[],
  categoryId: number,
): UserCategory[] => {
  return categories.map((category) => {
    if (category.id === categoryId) {
      // If this is the selected category, update as needed
      return { ...category, is_selected: true };
    } else if (category.children?.some((child) => child.id === categoryId)) {
      // If the selected category is a child, select this category
      return {
        ...category,
        is_selected: true,
        children: checkCategoryAndParent(category.children, categoryId),
      };
    } else {
      // Otherwise, recurse into children
      return {
        ...category,
        children: checkCategoryAndParent(category.children, categoryId),
      };
    }
  });
};

const uncheckCategoryAndChildren = (
  categories: UserCategory[],
  categoryId: number,
): UserCategory[] => {
  return categories.map((category) => {
    if (category.id === categoryId) {
      // Deselect this category and all its children
      return {
        ...category,
        is_selected: false,
        children: deselectAllChildren(category.children),
      };
    } else {
      // Otherwise, recurse into children
      return {
        ...category,
        children: uncheckCategoryAndChildren(category.children, categoryId),
      };
    }
  });
};

const deselectAllChildren = (children: UserCategory[]): UserCategory[] => {
  return children.map((child) => ({
    ...child,
    is_selected: false,
    children: deselectAllChildren(child.children),
  }));
};
